import { call } from '@rental/contracts/client'
import { STAFF_PASSWORD_MIN_LENGTH } from '@rental/domain'
import { format } from '@rental/i18n'
import { Button, cn, Field } from '@rental/ui'
import { useNavigate } from '@tanstack/react-router'
import { type FormEvent, type RefObject, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import type { StaffSession } from '../access'
import { useAccess, useSession } from '../access-context'
import { api } from '../api'
import { OutcomeNotice } from '../components/OutcomeNotice'
import { PageHeader } from '../components/PageHeader'
import { PageHeading, ProblemNotice, StaffInput } from '../components/ui'
import { useLocale } from '../i18n'
import type { StaffProblem } from '../problems'
import { Frame } from './Frame'

type Which = 'current' | 'next' | 'confirm'

/**
 * S9 (Q-51): the current password, then a new one twice. The server asks for
 * the current one again: a screen left unlocked on the counter must not let
 * someone else choose it. What the app can tell before asking (a field
 * empty, a password too short, two that differ) is said under that field,
 * which takes the focus; the rest (the e-mail, the same password again, a
 * wrong current one) is the server's answer, said under the form. Nothing
 * typed stays in the page once it has done its work.
 */
function PasswordForm({
  currentLabel,
  onChanged,
  className,
}: {
  currentLabel: string
  onChanged: (session: StaffSession) => void
  className?: string
}) {
  const { messages: m } = useLocale()
  const { takeOver } = useAccess()
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<{ field: Which; text: string } | null>(null)
  const [problem, setProblem] = useState<StaffProblem | null>(null)
  const [busy, setBusy] = useState(false)
  const fields: Record<Which, RefObject<HTMLInputElement | null>> = {
    current: useRef<HTMLInputElement>(null),
    next: useRef<HTMLInputElement>(null),
    confirm: useRef<HTMLInputElement>(null),
  }

  function refuse(field: Which, text: string) {
    flushSync(() => setError({ field, text }))
    fields[field].current?.focus()
  }

  async function submit(event: FormEvent) {
    event.preventDefault()
    setProblem(null)
    if (current.length === 0) return refuse('current', m.password.missingCurrent)
    if (next.length < STAFF_PASSWORD_MIN_LENGTH) return refuse('next', m.password.short)
    if (next !== confirm) return refuse('confirm', m.password.mismatch)
    setError(null)
    setBusy(true)
    const result = await call(
      api.PUT('/v1/staff/me/password', {
        body: { currentPassword: current, newPassword: next },
      }),
    )
    setBusy(false)
    if (result.ok) {
      setCurrent('')
      setNext('')
      setConfirm('')
      onChanged(result.data)
      return
    }
    if (takeOver(result.problem)) return
    // The password asked for again, not right: said under it, as the app's own checks are.
    if (result.problem.kind === 'api' && result.problem.code === 'credentials_invalid') {
      return refuse('current', m.password.wrongCurrent)
    }
    setProblem(result.problem)
  }

  const errorFor = (field: Which) => (error?.field === field ? error.text : null)
  return (
    <form onSubmit={submit} noValidate className={cn('flex flex-col gap-4', className)}>
      <Field label={currentLabel} required error={errorFor('current')}>
        {(control) => (
          <StaffInput
            {...control}
            ref={fields.current}
            type="password"
            autoComplete="current-password"
            value={current}
            onChange={(event) => setCurrent(event.target.value)}
          />
        )}
      </Field>
      <Field label={m.password.next} hint={m.password.nextHint} required error={errorFor('next')}>
        {(control) => (
          <StaffInput
            {...control}
            ref={fields.next}
            type="password"
            autoComplete="new-password"
            maxLength={200}
            value={next}
            onChange={(event) => setNext(event.target.value)}
          />
        )}
      </Field>
      <Field label={m.password.confirm} required error={errorFor('confirm')}>
        {(control) => (
          <StaffInput
            {...control}
            ref={fields.confirm}
            type="password"
            autoComplete="new-password"
            maxLength={200}
            value={confirm}
            onChange={(event) => setConfirm(event.target.value)}
          />
        )}
      </Field>
      <ProblemNotice problem={problem} />
      <div className="flex flex-wrap gap-3">
        <Button type="submit" variant="staff" busy={busy}>
          {m.password.submit}
        </Button>
      </div>
    </form>
  )
}

/**
 * "Cambia password", beside "Cambia PIN": for anyone unlocked, however they
 * unlocked, since the current password is asked for again. The outcome is
 * said where the form stays, and the form is empty for another change.
 */
export function ChangePasswordScreen() {
  const { messages: m } = useLocale()
  const { transition } = useAccess()
  const [changed, setChanged] = useState(0)
  return (
    <>
      <PageHeader title={m.password.title} intro={m.password.intro} />
      {changed > 0 ? (
        <OutcomeNotice key={changed}>
          <p>{m.password.done}</p>
        </OutcomeNotice>
      ) : null}
      <PasswordForm
        currentLabel={m.password.current}
        className="glass-pane max-w-xl rounded-card p-4 sm:p-6"
        onChanged={(session) => {
          transition({ state: 'unlocked', session })
          setChanged((count) => count + 1)
        }}
      />
    </>
  )
}

/**
 * S9: while the password is one someone else chose (a new account, a reset),
 * the server answers nothing but this change, so the app shows nothing else:
 * no menu, no screen behind it, on the card of the screens before the PIN.
 * Whose session it is stays in sight, for a shared iPad. Once it is changed
 * the address asked for opens; a new person, who has no PIN yet, chooses one
 * next, within the password sign-in's ten minutes. Locking is the way out.
 */
export function ForcedPasswordScreen() {
  const { messages: m } = useLocale()
  const { transition, lock } = useAccess()
  const session = useSession()
  const navigate = useNavigate()
  const role = (m.roles as Readonly<Record<string, string>>)[session.user.role] ?? session.user.role
  return (
    <Frame device={session.device.name}>
      <div className="flex flex-col gap-1">
        <PageHeading className="text-xl">{m.password.forcedTitle}</PageHeading>
        <p className="text-sm font-medium">
          {format(m.nav.signedInAs, { name: session.user.displayName, role })}
        </p>
        <p className="text-sm text-text-muted">{m.password.forcedIntro}</p>
      </div>
      <PasswordForm
        currentLabel={m.password.received}
        onChanged={(changed) => {
          transition({ state: 'unlocked', session: changed })
          if (changed.pinMissing) void navigate({ to: '/pin' })
        }}
      />
      <div className="flex justify-end">
        <Button
          variant="link"
          className="touch-target min-h-0 text-sm no-underline"
          onClick={() => void lock('locked')}
        >
          {m.nav.lock}
        </Button>
      </div>
    </Frame>
  )
}
